import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { mapResponse } from './helpers';
import {
  ApiVaultDepositIntentRequest,
  ApiVaultDepositRequest,
  ApiVaultWithdrawIntentRequest,
  ApiVaultWithdrawRequest,
} from '../openapi';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';
import { useHasRole } from './access';
import { useEffect } from 'react';

export function useVaultsQuery() {
  const client = useApiClient();
  const isViewer = useHasRole('VIEWER');
  const query = useQuery({
    queryKey: ['vaults'],
    queryFn: ({ signal }) =>
      client
        .GET('/vaults', { signal })
        .then(mapResponse)
        .then(({ vaults }) => vaults),
    retry: true,
    enabled: isViewer,
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    if (query.data) {
      for (const it of query.data) {
        queryClient.setQueryData(['vaults', it.id], it);
      }
    }
  }, [query.data, queryClient]);

  return query;
}

export function useVaultQuery({ vaultId }: { vaultId: string | null | undefined }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['vaults', vaultId],
    queryFn: () =>
      client.GET('/vaults/{id}', { params: { path: { id: vaultId! } } }).then(mapResponse),
    enabled: Boolean(vaultId),
  });
}

export function useVaultProductQuery() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['vaults-product', 'earning'],
    queryFn: ({ signal }) => client.GET('/vaults/products/earning', { signal }).then(mapResponse),
  });
}

export function useVaultCreateMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['vaults'],
    mutationFn: (body: { productId: string }) =>
      api
        .POST('/vaults', { body, headers: { 'Idempotency-Key': idempotencyKey } })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['vaults'],
      }),
  });
}

export function useVaultDepositIntentMutation() {
  const api = useApiClient();
  return useMutation({
    mutationKey: ['vaults', 'deposit', 'intent'],
    mutationFn: (request: ApiVaultDepositIntentRequest & { targetVaultId: string }) => {
      const { targetVaultId, ...body } = request;
      return api
        .POST('/vaults/{id}/deposit/intent', {
          body,
          params: { path: { id: targetVaultId } },
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse);
    },
    retry: 1,
    retryDelay: 500,
  });
}

export function useVaultDepositMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['vaults', 'deposit', idempotencyKey],
    mutationFn: ({ targetVaultId, ...body }: ApiVaultDepositRequest & { targetVaultId: string }) =>
      api
        .POST('/vaults/{id}/deposit', {
          body,
          params: { path: { id: targetVaultId }, header: { 'Idempotency-Key': idempotencyKey } },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () =>
      Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['vaults'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
      ]),
  });
}

export function useVaultWithdrawIntentMutation() {
  const api = useApiClient();
  return useMutation({
    mutationKey: ['vaults', 'withdraw', 'intent'],
    mutationFn: ({
      sourceVaultId,
      ...body
    }: ApiVaultWithdrawIntentRequest & { sourceVaultId: string }) =>
      api
        .POST('/vaults/{id}/withdraw/intent', {
          body,
          params: { path: { id: sourceVaultId } },
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse),
    retry: 1,
    retryDelay: 500,
  });
}

export function useVaultWithdrawMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['vaults', 'withdraw', idempotencyKey],
    mutationFn: ({ sourceVaultId, ...body }: ApiVaultWithdrawRequest & { sourceVaultId: string }) =>
      api
        .POST('/vaults/{id}/withdraw', {
          body,
          params: { path: { id: sourceVaultId }, header: { 'Idempotency-Key': idempotencyKey } },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () =>
      Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['vaults'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
      ]),
  });
}

export function useVaultTerminateMutation({
  vaultId,
  idempotencyKey,
}: {
  vaultId: string;
  idempotencyKey: string;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['vaults', vaultId],
    mutationFn: () =>
      api
        .POST('/vaults/{id}/terminate', {
          body: undefined as never,
          params: { path: { id: vaultId } },
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(() => null),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['vaults'],
      }),
  });
}
